import { type EligibilityContext } from '@heidi/runtime/shell';
import { type SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useWorkspaceId } from '@heidi/workspace/data';
import { useRef } from 'react';

import type { UserBillingInfo } from '../../../contracts/types/commerce.types';
import type { CreditAllowanceResponse } from '../../../contracts/types/credit-allowance.types';
import { type isCreditAllowanceThresholdBillingPlanResolved } from '../../settings/get-credit-allowance-threshold-billing-plan-key';
import type { CreditAllowanceThresholdConsumedState } from './creditAllowanceThresholdStorage';

export type SideNavCreditUsageThresholdPopoverLaunchGate = Pick<
  EligibilityContext,
  'isHeidi2OptInEnabled' | 'isUserManaged'
> & {
  newUserTier: SubscriptionTier | null | undefined;
  /** False while a workspace switch is hard-reloading the app. */
  isWorkspaceIdSettled: boolean;
  /** The app build check, read where `isAppleCompliantBuild()` is read today. */
  isAppleCompliantBuild: () => boolean;
};

type CreditUsageLaunchBillingInfo =
  | (NonNullable<Parameters<typeof isCreditAllowanceThresholdBillingPlanResolved>[0]> &
      Partial<Pick<UserBillingInfo, 'heidi_version'>>)
  | null
  | undefined;

/**
 * A workspace switch writes the new id and then hard-reloads (`workspace-top-menu`),
 * so the id changing mid-session means this document is on its way out — the flag
 * resync for the new workspace must not flash the popover before the reload lands.
 * `null -> id` is boot hydration, not a switch.
 */
export function useWorkspaceIdSettledForCreditUsagePopoverLaunch(): boolean {
  const workspaceId = useWorkspaceId();
  const hydratedWorkspaceIdRef = useRef<string | null>(null);

  if (workspaceId && !hydratedWorkspaceIdRef.current) {
    hydratedWorkspaceIdRef.current = workspaceId;
  }

  return !workspaceId || hydratedWorkspaceIdRef.current === workspaceId;
}

/**
 * Threshold auto-queue is retired: side-nav opens the upgrade modal instead of
 * surfacing usage after the complimentary window. Signature kept so the launch
 * modal wiring and callers stay stable.
 */
export function computeShouldQueueSideNavCreditUsageThresholdPopover(
  gate: SideNavCreditUsageThresholdPopoverLaunchGate,
  billingInfo: CreditUsageLaunchBillingInfo,
  allowance: CreditAllowanceResponse | null | undefined,
  consumedThresholds: CreditAllowanceThresholdConsumedState = {}
): boolean {
  void gate;
  void billingInfo;
  void allowance;
  void consumedThresholds;
  return false;
}
